import React, { Context, Fragment } from 'react'
import { TabContextProps } from './tab-context'
import { tabHeadingsRightPadding, TabHeadingConfig } from './tab-headings'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { CSSTransition } from 'react-transition-group'

const animatedBarHeight = 3
const timeout = 500
const transitionName = 'tab-heading-transition'

interface TabHeadingsFooterProps<TTabs extends string> {
  readonly showHorizontalRule?: boolean
  readonly renderExtra?: () => any
  readonly listClassName?: string
  readonly context: Context<TabContextProps<TTabs>>
  readonly tabHeadingConfigs: Array<TabHeadingConfig<TTabs>>
}

/**
 * This is the horizontal rule and animated sliding bar that is displayed immediately underneath the tab headings.
 * This is a separate component from TabHeadings because there is a use case that requires a slightly more complex
 * layout.
 * Depends on TabManager to provide context.
 */
export function TabHeadingsFooter<TTabs extends string>({
  showHorizontalRule = true,
  context: TabContext,
  tabHeadingConfigs
}: TabHeadingsFooterProps<TTabs>) {
  return (
    <TabContext.Consumer>
      {({
        previousTabID: prevID,
        currentTabID: currID,
        transitionToggleFlag,
        tabHeadingWidths
      }) => {
        // Default the tab to current tab on first launch
        if (!prevID) {
          prevID = currID
        }

        const tabOrder = tabHeadingConfigs.map((c) => c.id)
        const widths = tabOrder
          .map((tabID) => {
            return tabHeadingWidths[tabID]
          })
          .reduce<number[]>((acc, curr) => {
            return [...acc, curr]
          }, [])

        const prevOrderIndex = !!prevID ? tabOrder.indexOf(prevID) : 0
        const currOrderIndex = tabOrder.indexOf(currID)

        const prevWidthsMaxIndex = !!prevID ? prevOrderIndex : 0
        const startX = !!prevID
          ? widths.slice(0, prevWidthsMaxIndex).reduce((a, c) => a + c, 0) +
            tabHeadingsRightPadding * prevWidthsMaxIndex
          : 0
        const startW = !!prevID ? widths[prevWidthsMaxIndex] : widths[0]

        const currWidthsMaxIndex = currOrderIndex
        const endX =
          widths.slice(0, currWidthsMaxIndex).reduce((a, c) => a + c, 0) +
          tabHeadingsRightPadding * currOrderIndex
        const endW = widths[currWidthsMaxIndex]
        return (
          <Fragment>
            {showHorizontalRule === true && (
              <div
                className={css`
                  border-top: 2px solid ${theme.color.neutral30};
                `}
              />
            )}
            {Object.keys(tabHeadingWidths).length ===
              tabHeadingConfigs.length && (
              <div
                className={css`
                  display: block;
                `}
              >
                <CSSTransition
                  in={transitionToggleFlag}
                  timeout={timeout}
                  classNames={transitionName}
                >
                  <div
                    className={cx(
                      css`
                        height: ${animatedBarHeight}px;
                        display: block;
                        background-color: ${theme.color.teal50};

                        transform: translateX(${startX}px);
                        width: ${startW}px;

                        &.${transitionName}-enter, &.${transitionName}-exit {
                          transform: translateX(${startX}px);
                          width: ${startW}px;
                        }
                        &.${transitionName}-enter-active,
                          &.${transitionName}-exit-active {
                          transform: translateX(${endX}px);
                          width: ${endW}px;
                          transition: all ${timeout}ms ease-out;
                        }
                        &.${transitionName}-enter-done,
                          &.${transitionName}-exit-done {
                          transform: translateX(${endX}px);
                          width: ${endW}px;
                        }
                      `
                    )}
                  />
                </CSSTransition>
              </div>
            )}
          </Fragment>
        )
      }}
    </TabContext.Consumer>
  )
}
